export interface TurnstileVerificationResult {
  success: boolean;
  error?: string;
  errorCodes?: string[];
}

const TURNSTILE_TEST_SITE_KEY = "1x00000000000000000000AA";

/**
 * Site key pública de produção (app.elinia.ai). Chave pública; o segredo fica no Edge Function.
 * Se o widget mostrar erro 400020 / 400 no iframe: confira no painel Cloudflare se a key está ativa
 * e se o hostname da página está em Hostname Management do widget (incl. localhost para dev com esta key).
 */
const TURNSTILE_PRODUCTION_SITE_KEY =
  "0x4AAAAAAClwI5GaNw1NlCeP";

/**
 * Mensagem para o usuário quando o widget Turnstile chama error-callback.
 * @see https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
 */
export function getTurnstileWidgetErrorUserMessage(
  errorCode?: string | number,
): string {
  const code = String(errorCode ?? "").trim();
  // 400020 costuma significar site key inválida/inativa ou parâmetro rejeitado (a Cloudflare documenta esta família como invalid sitekey).
  if (code === "400020" || /^400\d{3}$/.test(code)) {
    return "Chave Turnstile inválida ou domínio não autorizado. No Cloudflare: confira o site key, ative o widget e adicione este hostname em Hostnames (ex.: app.elinia.ai e, para testar no PC, localhost).";
  }
  if (code === "110200") {
    return "Este domínio não está na lista de hostnames permitidos do Turnstile no Cloudflare.";
  }
  if (code === "110100" || code === "110110") {
    return "Chave do Turnstile inválida ou inativa. Verifique no painel Cloudflare.";
  }
  if (code.startsWith("300") || code.startsWith("600")) {
    return "Não foi possível concluir a verificação. Tente recarregar a página.";
  }
  return "Erro na verificação de segurança. Tente novamente.";
}

const VERIFY_TURNSTILE_MAX_ATTEMPTS = 3;
const NETWORK_USER_MESSAGE =
  "Falha de conexão na verificação de segurança. Verifique sua internet e tente novamente.";

type VerifyTurnstileResponseBody = {
  success?: boolean;
  error?: string;
  errorCodes?: string[];
};

function isNetworkInvokeError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { name?: string; message?: string };
  return (
    e.name === "FunctionsFetchError" ||
    (typeof e.message === "string" &&
      e.message.includes("Failed to send a request to the Edge Function"))
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Fallback when `functions.invoke` fails at the transport layer (common on flaky mobile networks).
 * Uses the same URL and anon key as the Supabase JS client.
 */
async function verifyTurnstileViaHttp(
  token: string,
): Promise<
  | { ok: true; body: VerifyTurnstileResponseBody }
  | { ok: false; reason: "network" | "parse"; cause?: unknown }
> {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as
    | string
    | undefined;
  if (!baseUrl?.trim() || !anonKey?.trim()) {
    return {
      ok: false,
      reason: "network",
      cause: new Error("Missing Supabase env"),
    };
  }

  const url = `${baseUrl.replace(/\/$/, "")}/functions/v1/verify-turnstile`;

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
        apikey: anonKey,
      },
      body: JSON.stringify({ token }),
    });

    let body: VerifyTurnstileResponseBody;
    try {
      body = (await res.json()) as VerifyTurnstileResponseBody;
    } catch (parseErr) {
      return { ok: false, reason: "parse", cause: parseErr };
    }

    return { ok: true, body };
  } catch (cause) {
    return { ok: false, reason: "network", cause };
  }
}

function isLocalhostRuntime(): boolean {
  if (typeof window === "undefined") return false;
  const { hostname } = window.location;
  return (
    hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1"
  );
}

function isLovablePreviewRuntime(): boolean {
  if (typeof window === "undefined") return false;
  const { hostname } = window.location;
  return (
    hostname.endsWith(".lovableproject.com") ||
    hostname.includes("id-preview--")
  );
}

export function shouldUseTurnstile(): boolean {
  return !isLocalhostRuntime() && !isLovablePreviewRuntime();
}

/**
 * Verifica o token do Turnstile no servidor
 */
export async function verifyTurnstileToken(
  token: string,
): Promise<TurnstileVerificationResult> {
  if (!shouldUseTurnstile()) {
    if (import.meta.env.DEV) {
      console.info(
        "Turnstile verification bypassed for local/preview runtime.",
      );
    }
    return { success: true };
  }

  const { supabase } = await import("@/integrations/supabase/client");

  const mapInvokeFailure = (
    data: VerifyTurnstileResponseBody | null,
  ): TurnstileVerificationResult => ({
    success: false,
    error: data?.error || "Verificação falhou. Tente novamente.",
    errorCodes: data?.errorCodes,
  });

  for (let attempt = 0; attempt < VERIFY_TURNSTILE_MAX_ATTEMPTS; attempt++) {
    try {
      const { data, error } = await supabase.functions.invoke(
        "verify-turnstile",
        {
          body: { token },
        },
      );

      if (!error) {
        const body = data as VerifyTurnstileResponseBody | null;
        if (body?.success) {
          return { success: true };
        }
        return mapInvokeFailure(body);
      }

      if (isNetworkInvokeError(error)) {
        if (attempt < VERIFY_TURNSTILE_MAX_ATTEMPTS - 1) {
          await sleep(400 * (attempt + 1));
          continue;
        }
        break;
      }

      const err = error as { message?: string };
      const errorMessage =
        typeof err.message === "string" &&
        err.message.includes("Failed to send a request to the Edge Function")
          ? NETWORK_USER_MESSAGE
          : err.message || "Erro ao verificar token";

      if (import.meta.env.DEV) {
        console.error("Turnstile verification error:", error);
      } else {
        console.error("Turnstile verification error:", err.message ?? error);
      }

      return {
        success: false,
        error: errorMessage,
      };
    } catch (error) {
      if (attempt < VERIFY_TURNSTILE_MAX_ATTEMPTS - 1) {
        await sleep(400 * (attempt + 1));
        continue;
      }
      if (import.meta.env.DEV) {
        console.error("Turnstile verification error:", error);
      }
      break;
    }
  }

  const direct = await verifyTurnstileViaHttp(token);
  if (direct.ok) {
    const { body } = direct;
    if (body.success) {
      return { success: true };
    }
    return mapInvokeFailure(body);
  }

  if (import.meta.env.DEV) {
    console.error(
      "Turnstile verification error (invoke + direct fetch failed):",
      direct,
    );
  } else {
    console.error(
      "Turnstile verification error: network failure after retries",
    );
  }

  return {
    success: false,
    error: NETWORK_USER_MESSAGE,
  };
}

/** Match any Turnstile loader URL (base path or with ?query). */
const TURNSTILE_API_SRC_MARKER =
  "challenges.cloudflare.com/turnstile/v0/api.js";

function findTurnstileScriptEl(): HTMLScriptElement | null {
  for (const el of document.querySelectorAll("script[src]")) {
    const src = el.getAttribute("src") ?? "";
    if (src.includes(TURNSTILE_API_SRC_MARKER)) {
      return el as HTMLScriptElement;
    }
  }
  return null;
}

/**
 * Carrega o script do Turnstile dinamicamente
 */
export function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const existing = findTurnstileScriptEl();
    if (existing) {
      let settled = false;
      const checkInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkInterval);
          clearTimeout(failTimer);
          if (!settled) {
            settled = true;
            resolve();
          }
        }
      }, 100);

      const failTimer = setTimeout(() => {
        clearInterval(checkInterval);
        if (!settled && !window.turnstile) {
          settled = true;
          reject(new Error("Turnstile script loaded but API not available"));
        }
      }, 10000);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setTimeout(() => {
        if (window.turnstile) {
          resolve();
        } else {
          reject(new Error("Turnstile script loaded but API not available"));
        }
      }, 100);
    };
    script.onerror = () => reject(new Error("Failed to load Turnstile script"));
    document.head.appendChild(script);
  });
}

/**
 * Site key do Turnstile: localhost usa key de teste; preview Lovable desliga;
 * `VITE_TURNSTILE_SITE_KEY` tem prioridade; em build de produção sem env, usa key embutida.
 */
export function getTurnstileSiteKey(): string {
  if (isLocalhostRuntime()) {
    if (import.meta.env.DEV) {
      console.info("Using Cloudflare Turnstile test site key on localhost.");
    }
    return TURNSTILE_TEST_SITE_KEY;
  }

  if (isLovablePreviewRuntime()) {
    if (import.meta.env.DEV) {
      console.info("Skipping Turnstile on Lovable preview domain.");
    }
    return "";
  }

  const envKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "").trim();
  if (envKey) {
    if (import.meta.env.DEV) {
      console.log(
        "✅ VITE_TURNSTILE_SITE_KEY configurada:",
        envKey.substring(0, 10) + "...",
      );
    }
    return envKey;
  }

  // Build de produção: usa site key embutida (domínio autorizado no Cloudflare).
  if (import.meta.env.PROD) {
    return TURNSTILE_PRODUCTION_SITE_KEY;
  }

  if (import.meta.env.DEV) {
    console.warn(
      "⚠️ VITE_TURNSTILE_SITE_KEY não configurada — bypass do Turnstile (apenas em dev).",
    );
  }
  return "";
}
